import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@core/appconfig.service';
import { ErrorHandlingService } from '@core/services/error-handling/error-handling.service';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { CardGroupModel } from '@ece-models/card-group.model';
import { CardModel } from '@ece-models/card.model';
import { DockThemeModel } from '@ece-models/dock-theme.model';
import { DockModel } from '@ece-models/dock.model';
import LicenseActivity from '@ece-models/license-activity.model';
import PlannerGroupRequest from '@ece-models/planner-group-request.model';
import PlannerGroupResponse from '@ece-models/planner-group-response.model';
import { PlannerGroupThumbnailModel } from '@ece-models/planner-group-thumbnail.model';
import PlannerProductCard from '@ece-models/planner-product-card';
import PlannerProductNotes from '@ece-models/planner-product-notes.model';
import { PlannerModel } from '@ece-models/planner.model';
import { ProductActivationModel } from '@ece-models/product-activation.model';
import { ApiResponse } from '@models/api-response';
import { KeyValuePair } from '@models/key-value-pair';
import { copyObject } from '@shared/zb-object-helper/object-helper';
import {
  instantiateApiResponseFromArray,
  instantiateApiResponseFromJson
} from '@shared/zb-rxjs-operators/rxjs-operators';
import { BehaviorSubject, Observable, map, catchError, of, mergeMap, retry } from 'rxjs';

/** Contains State and API calls. */
@Injectable({
  providedIn: 'root'
})
export class EceApiService implements StatefulService {
  selectedCurrentDay$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private errorHandlingService: ErrorHandlingService,
  ) {
  }

  getPlannerModel(productType: string, variantType: string, beginningDateAsString: string, userId: string): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/product/${productType}/variant/${variantType}/week/${beginningDateAsString}/user/${userId}`;
    return this.http.put(url, {}, { observe: 'response' })
      .pipe(
        mergeMap((response: HttpResponse<any>) => {
          if (response.status === 204) {
            throw new Error('Planner content not ready');
          }
          return of(response as any);
        }),
        retry({ count: 2, delay: 2000, resetOnSuccess: true }),
        map(response => response.body),
        instantiateApiResponseFromJson(PlannerModel),
        map((res: any) => res.response),
      );
  }

  updatePlannerGroups(weekId: string, plannerGroupThumbnails: PlannerGroupThumbnailModel[]): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/week/${weekId}/card-action-request`;
    return this.http.patch(url, { plannerHorizontalRows: plannerGroupThumbnails })
      .pipe(
        instantiateApiResponseFromJson(PlannerModel),
        map((res: any) => res.response),
      );
  }

  // /planner/week/{weekId}/card-action-request

  // Example with commented code left in on purpose.
  // exampleMockDataRequest(
  // productType: string, beginningDateAsString: string, userId: string): Observable<PlannerModel> {
  //   // const url: string = `/planner/product/${productType}/week/${beginningDateAsString}/user/${userId}`;
  //   // return this.http.get(url)
  //   return this.mockApiService.getMockEcePlannerData(productType, beginningDateAsString, userId)
  //     .pipe(
  //       map((res: any) => copyObject(res.response, PlannerModel)),
  //     );
  // }

  /**
   * Adds a card to the planner when dragged from the dock
   * @param weekId as string
   * @param params request body of type KeyValuePair
   * @returns Observable of type PlannerModel
   */
  addCardToPlanner(weekId: string, params: KeyValuePair): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/week/${weekId}/card`;
    return this.http.post(url, params)
      .pipe(
        map((res: any) => copyObject(res, PlannerModel)),
      );
  }

  /**
   * Accept license activity.
   * @param params as LicenseActivity
   * @returns Observable of type LicenseActivity
   */
  acceptUserLicenseActivity(params: LicenseActivity): Observable<LicenseActivity> {
    const url: string = `${this.appConfig.apiUrl}/user/license-activity`;
    return this.http.post(url, params)
      .pipe(
        map((res: any) => copyObject(res, LicenseActivity)),
      );
  }

  /**
   * Reset week
   * @param weekId as string
   * @returns Observable of type PlannerModel
   */
  resetWeek(weekId: string): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/week/${weekId}/card`;
    return this.http.delete(url)
      .pipe(
        instantiateApiResponseFromJson(PlannerModel),
        map((res: any) => res.response),
      );
  }

  pasteWeek(planId: string, copyFromSundayDate: any, pasteToSundayDate: any): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/${planId}/calendar/copy-paste`;
    return this.http.put(url, { dateFrom: copyFromSundayDate, dateTo: pasteToSundayDate })
      .pipe(
        instantiateApiResponseFromJson(PlannerModel),
        map((res: any) => res.response),
      );
  }

  /**
   * Reorders a card on the planner via drag-and-drop
   * @param weekCardId as string
   * @param params as KeyValuePair (toPlannerGroupId, toDayOfWeek, weight)
   * @returns PlannerModel
   */
  reorderCardsInPlanner(weekCardId: string, params: KeyValuePair): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/week-plan-card/${weekCardId}/reorder`;
    return this.http.patch(url, params)
      .pipe(
        map((res: any) => copyObject(res, PlannerModel)),
      );
  }

  /**
   * Removes a card from the planner
   * @param plannerCardId as string
   * @returns Observable of type PlannerModel
   */
  removeCardFromPlanner(plannerCardId: string): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/week-plan-card/${plannerCardId}>`;
    return this.http.delete(url)
      .pipe(
        map((res: any) => copyObject(res, PlannerModel)),
      );
  }

  /**
   * Gets the planner product card for display.
   *
   * @param {string} productType The product type usually derived from the Product Line.
   * @param {string} variantType The variant type of the product.
   * @param {string} userId globally-unique identifier
   * @param {string} cardId globally-unique identifier.
   * @param {string} topicKey Important. This is NOT a topic key. This does not conform to the specification for topic
   *                          keys and should not be considered a topic key.
   */
  getPlannerProductCard(
    productType: string, variantType: string, userId: string, cardId: string, topicKey: string
  ): Observable<ApiResponse<PlannerProductCard>> {
    const url = `${this.appConfig.apiUrl}/planner/product/${productType}/variant/${variantType}/user/${userId}/card/${cardId}/topicKey/${topicKey}`;
    return this.http.get(url).pipe(instantiateApiResponseFromJson(PlannerProductCard));
  }

  updatePlannerProductCardNotes(
    planId: string, providedCardId: string, notes: string
  ): Observable<ApiResponse<PlannerProductNotes>> {
    const url = `${this.appConfig.apiUrl}/planner/${planId}/provided-card/${providedCardId}`;
    return this.http.patch(url, { notes })
      .pipe(
        instantiateApiResponseFromJson(PlannerProductNotes),
        catchError((err) => {
          console.log(err);
          this.errorHandlingService.error$.next(err);
          throw err; //rethrow
        })
      );
  }

  /**
   * Retrieves theme information for the dock
   * @param ProductType as string
   * @returns PlannerThemeNavigationItem (not yet available as of 9-5)
   */
  getDockThemes(productType: string, variantType: string, userId: string): Observable<DockThemeModel[]> {
    const url: string = `${this.appConfig.apiUrl}/planner/product/${productType}/variant/${variantType}/user/${userId}/theme`;
    return this.http.get(url)
      .pipe(
        instantiateApiResponseFromArray(DockThemeModel),
        map(res => res.response),
      );
  }

  /**
   * Get the available groups
   * @param planId as string
   * @returns Observable<CardGroupModel>
   */
  getGroups(planId: string): Observable<CardGroupModel[]> {
    const url: string = `${this.appConfig.apiUrl}/planner/${planId}/group`;
    return this.http.get(url)
      .pipe(
        instantiateApiResponseFromArray(CardGroupModel),
        map(res => res.response)
      );
  }

  /**
   * Creates a new group
   * @param planId as string
   * @param body
   * @returns unknown
   */
  createGroup(planId: string, body: PlannerGroupRequest): Observable<ApiResponse<PlannerGroupResponse>> {
    const url: string = `${this.appConfig.apiUrl}/planner/${planId}/group`;
    return this.http.patch(url, body)
      .pipe(
        instantiateApiResponseFromJson(PlannerGroupResponse),
        catchError((err) => {
          this.errorHandlingService.error$.next(err);
          throw err; //rethrow
        })
      );
  }

  /**
   * Edits an existing group
   * @param plannerGroupId as string
   * @param request as KeyValuePair(groupName, blockDescription, icon)
   * @returns unknown
   */
  editGroup(plannerGroupId: string, params: KeyValuePair): unknown {
    const url: string = `${this.appConfig.apiUrl}/planner/group/${plannerGroupId}`;
    return this.http.patch(url, params)
      .pipe(
        map((res: any) => copyObject(res, null))
      );
  }

  /**
   * Delete an existing group
   * @param plannerGroupId as string
   * @returns unknown
   */
  deleteGroup(plannerGroupId: string): any {
    const url: string = `${this.appConfig.apiUrl}/planner/group/${plannerGroupId}`;
    return this.http.delete(url)
      .pipe(
        map(res => res)
      );
  }

  /**
   * Reorders groups on the planner
   * @param plannerGroupId as string
   * @param index as number
   * @returns PlannerModel
   */
  reorderGroup(plannerGroupId: string, index: number): Observable<PlannerModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/group/${plannerGroupId}/reorder`;
    return this.http.patch(url, `${index}`)
      .pipe(
        map((res: PlannerModel) => copyObject(res, PlannerModel))
      );
  }

  /**
   * Gets a single card
   * @param customCardId as string
   * @returns response as CardModel if successful or Observable flagged as error along with error information
   */
  getCustomCard(customCardId: string): Observable<any> {
    const url: string = `${this.appConfig.apiUrl}/planner/custom-card/${customCardId}`;
    return this.http.get(url)
      .pipe(
        map((res: CardModel) => copyObject(res, CardModel)));
  }

  /**
   * Creates a new custom card
   * @param planId as string
   * @param body as KeyValuePair(plannerGroupId, title, directions, notes)
   * @returns response if successful else error is handled by ErrorHandlingService and returned as ApiResponse object
   */
  createUpdateCustomCard(planId: string, customCardObject: Record<string, string>): Observable<any> {
    const url: string = `${this.appConfig.apiUrl}/planner/${planId}/custom-card`;

    return this.http.patch(url, customCardObject)
      .pipe(
        map((res: CardModel) => copyObject(res, CardModel)),
        // Catch any errors so that server-side validation errors, such as duplicate title, can be displayed to the user
        // Continue with ErrorHandlingService for standard error logging flow
        catchError((error) => {
          this.errorHandlingService.error$.next(error);
          return of(new ApiResponse<CardModel>(false, { response: null, messages: [error.error] }));
        }
        ));
  }

  /**
   * Deletes a custom card
   * @param customCardId
   * @returns APIResponse with response of true if successful or response of null if errored
   */
  deleteCustomCard(customCardId: string): Observable<ApiResponse<any>> {
    const url: string = `${this.appConfig.apiUrl}/planner/custom-card/${customCardId}`;
    return this.http.delete(url)
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] })),
        // Catch any errors so that a custom error message can be displayed to the user
        // Continue with ErrorHandlingService for standard error logging flow
        catchError((error) => {
          this.errorHandlingService.error$.next(error);
          return of(new ApiResponse<CardModel>(false, { response: null, messages: [error.error] }));
        })
      );
  }

  searchForDockModel(productType: string, variantType: string, userId: string, searchTerm: string): Observable<DockModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/product/${productType}/variant/${variantType}/lesson-cards/user/${userId}`;
    const params: HttpParams = new HttpParams().set('criteria', searchTerm);
    return this.http.patch(url, {}, { params })
      .pipe(
        map((res: any) => copyObject(res.response, DockModel)),
      );
  }

  getDockModelByThemeId(productType: string, variantType: string, userId: string, themeId: string): Observable<DockModel> {
    const url: string = `${this.appConfig.apiUrl}/planner/product/${productType}/variant/${variantType}/lesson-cards/user/${userId}`;
    return this.http.patch(url, { themeId })
      .pipe(
        map((res: any) => copyObject(res.response, DockModel))
      );
  }

  getProductActivationPeriod(productType: string, variantType: string): Observable<ProductActivationModel> {
    const url: string = `${this.appConfig.apiUrl}/product/${productType}/variant/${variantType}/calendar-period`;
    return this.http.get(url)
      .pipe(
        map((res: any) => copyObject(res.response, ProductActivationModel))
      );
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.selectedCurrentDay$.next(null);
  }
}
